/**
 * Cookie-consent state and its mapping to Google Consent Mode v2.
 *
 * This is the single source of truth shared by the cookie banner, the privacy
 * settings form, and the Google Tag Manager bootstrap. Keep the localStorage
 * shape (`aient_cookie_consent`) and the Consent Mode mapping here so the banner,
 * the settings form, and the pre-GTM default script can never drift apart.
 *
 * Current policy: analytics and marketing/ads are both denied until the visitor
 * opts in (prior consent, as the Swedish LEK / ePrivacy rules and our privacy
 * policy require). See docs/marketing/channels/web/2026-10-22-consent-before-tracking.md.
 */

export interface CookiePreferences {
  /** Always-on necessary cookies; not user-controllable. */
  essential: boolean
  analytics: boolean
  marketing: boolean
}

declare global {
  interface Window {
    aientConsent?: {
      analytics: boolean
      marketing: boolean
      essential: boolean
    }
    /** Defined by the GTM container (or our consent-default shim). */
    gtag?: (...args: unknown[]) => void
    /** Meta Pixel, if loaded. */
    fbq?: (...args: unknown[]) => void
  }
}

export interface StoredConsent {
  version: string
  preferences: CookiePreferences
  timestamp: string
}

export const COOKIE_CONSENT_KEY = 'aient_cookie_consent'
export const COOKIE_CONSENT_VERSION = '1.0'

// POLICY: nothing non-essential runs before the visitor answers the banner.
// The banner, the settings form, and the Consent Mode default script all
// derive from this, so this one constant is the switch.
export const DEFAULT_PREFERENCES: CookiePreferences = {
  essential: true,
  analytics: false,
  marketing: false,
}

type ConsentSignal = 'granted' | 'denied'

/** Google Consent Mode v2 signal block. */
export interface ConsentState {
  analytics_storage: ConsentSignal
  ad_storage: ConsentSignal
  ad_user_data: ConsentSignal
  ad_personalization: ConsentSignal
}

/**
 * Map our coarse `analytics`/`marketing` toggles onto Consent Mode v2 signals.
 * `analytics` → analytics_storage; `marketing` → the three ad signals.
 */
export function toConsentState(prefs: CookiePreferences): ConsentState {
  const analytics: ConsentSignal = prefs.analytics ? 'granted' : 'denied'
  const ad: ConsentSignal = prefs.marketing ? 'granted' : 'denied'
  return {
    analytics_storage: analytics,
    ad_storage: ad,
    ad_user_data: ad,
    ad_personalization: ad,
  }
}

/**
 * Read the raw persisted consent record (no version filtering), or null when
 * absent/corrupt. Use this when you need to distinguish "never decided" from
 * "decided on an older version"; use {@link readStoredPreferences} when you just
 * want usable preferences.
 */
export function readConsentRecord(): StoredConsent | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(COOKIE_CONSENT_KEY)
    if (!raw) return null
    return JSON.parse(raw) as StoredConsent
  } catch {
    return null
  }
}

/** Read persisted preferences, falling back to defaults on absence/mismatch/corruption. */
export function readStoredPreferences(): CookiePreferences {
  if (typeof window === 'undefined') return DEFAULT_PREFERENCES
  try {
    const raw = window.localStorage.getItem(COOKIE_CONSENT_KEY)
    if (!raw) return DEFAULT_PREFERENCES
    const parsed = JSON.parse(raw) as Partial<StoredConsent>
    if (parsed?.version === COOKIE_CONSENT_VERSION && parsed.preferences) {
      // essential is always true regardless of what was stored
      return { ...DEFAULT_PREFERENCES, ...parsed.preferences, essential: true }
    }
  } catch {
    // Invalid stored data — fall through to defaults
  }
  return DEFAULT_PREFERENCES
}

/** Persist preferences in the canonical localStorage shape and return the record. */
export function persistPreferences(prefs: CookiePreferences): StoredConsent {
  const consent: StoredConsent = {
    version: COOKIE_CONSENT_VERSION,
    preferences: prefs,
    timestamp: new Date().toISOString(),
  }
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent))
  }
  return consent
}

/**
 * Push the current preferences to every in-page consumer:
 * the `window.aientConsent` bridge, the `aientConsentChange` event, Google
 * Consent Mode (`gtag('consent','update')`), and the Meta Pixel. Safe to call
 * when GTM/gtag/fbq are absent — the relevant branch is simply skipped.
 */
export function applyConsent(prefs: CookiePreferences): void {
  if (typeof window === 'undefined') return

  window.aientConsent = {
    analytics: prefs.analytics,
    marketing: prefs.marketing,
    essential: true,
  }

  window.dispatchEvent(new CustomEvent('aientConsentChange', { detail: prefs }))

  if (window.gtag) {
    window.gtag('consent', 'update', toConsentState(prefs))
  }

  if (window.fbq) {
    window.fbq('consent', prefs.marketing ? 'grant' : 'revoke')
  }

  clearDeniedTrackingCookies(prefs)
}

/** First-party cookies that Google Analytics (via sGTM) sets on our domain. */
const ANALYTICS_COOKIE = /^(_ga|_ga_.+|_gid|FPLC|FPID)$/
/** First-party cookies that the ad tags (Google Ads, Meta, LinkedIn) set on our domain. */
const MARKETING_COOKIE = /^(_gcl_.+|_fbp|_fbc|li_fat_id)$/

/**
 * Expire first-party tracking cookies for every denied category, so a visitor
 * who rejects (or withdraws) consent does not keep identifiers set earlier.
 * Runs on every {@link applyConsent}, so it also cleans up on later page views.
 *
 * Limits: an `HttpOnly` cookie (sGTM can set `FPID` that way) is invisible to
 * `document.cookie`; third-party cookies on `linkedin.com` are out of our reach.
 */
export function clearDeniedTrackingCookies(prefs: CookiePreferences): void {
  if (typeof document === 'undefined') return
  const denied = [
    ...(prefs.analytics ? [] : [ANALYTICS_COOKIE]),
    ...(prefs.marketing ? [] : [MARKETING_COOKIE]),
  ]
  if (denied.length === 0) return

  // A host-only cookie (no Domain attribute) and a domain cookie are separate
  // cookies, and a domain cookie is only removed by naming its domain. GA sets
  // `_ga` on `.aient.ai` even when served from aient.ai itself, so try no domain,
  // the full hostname, and each parent with at least two labels.
  const labels = window.location.hostname.split('.')
  const domains = [undefined, ...labels.slice(0, -1).map((_, i) => labels.slice(i).join('.'))]

  for (const entry of document.cookie.split(';')) {
    const name = entry.trim().split('=')[0]
    if (!name || !denied.some((pattern) => pattern.test(name))) continue
    for (const domain of domains) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domain ? `; domain=${domain}` : ''}`
    }
  }
}

/**
 * Inline JS injected `beforeInteractive` (before the GTM container loads) so
 * Google Consent Mode defaults are set first. Reads the same localStorage key
 * as the banner and mirrors {@link DEFAULT_PREFERENCES} when nothing is stored,
 * so a returning visitor who rejected analytics never gets a granted flash.
 *
 * Generated from the shared constants to guarantee it cannot drift from the
 * TypeScript path above.
 *
 * `wait_for_update: 500` gives the banner/settings a brief window to issue a
 * `consent('update')` before tags fire. Consent Mode only holds back Google
 * tags and tags with a consent check in GTM; any other tag in the container must
 * require `ad_storage` (marketing) or `analytics_storage` there itself.
 */
export function consentDefaultScript(): string {
  const d = DEFAULT_PREFERENCES
  return `
(function(){
  window.dataLayer = window.dataLayer || [];
  function gtag(){window.dataLayer.push(arguments);}
  window.gtag = window.gtag || gtag;
  var prefs = { analytics: ${d.analytics}, marketing: ${d.marketing} };
  try {
    var raw = window.localStorage.getItem(${JSON.stringify(COOKIE_CONSENT_KEY)});
    if (raw) {
      var parsed = JSON.parse(raw);
      if (parsed && parsed.version === ${JSON.stringify(COOKIE_CONSENT_VERSION)} && parsed.preferences) {
        prefs.analytics = !!parsed.preferences.analytics;
        prefs.marketing = !!parsed.preferences.marketing;
      }
    }
  } catch (e) {}
  var analytics = prefs.analytics ? 'granted' : 'denied';
  var ad = prefs.marketing ? 'granted' : 'denied';
  gtag('consent', 'default', {
    analytics_storage: analytics,
    ad_storage: ad,
    ad_user_data: ad,
    ad_personalization: ad,
    wait_for_update: 500
  });
  window.aientConsent = { analytics: prefs.analytics, marketing: prefs.marketing, essential: true };
})();
`.trim()
}

/**
 * Standard GTM bootstrap snippet, but loading the container from a caller-supplied
 * **same-origin relative** path (served first-party via sGTM, e.g.
 * `/collect/gtm.js`) instead of `www.googletagmanager.com`.
 *
 * Why relative and not an absolute URL: we run on multiple ccTLDs
 * (`aient.ai`, `aient.se`, `aient.dk`, …). A relative `src` resolves against the
 * CURRENT origin, so every domain loads — and sets cookies on — itself. An
 * absolute URL would pin all domains to one host, breaking per-domain
 * first-party serving (and `@next/third-parties`' `new URL()` rejects a relative
 * value, which is why this snippet is rendered directly rather than via that
 * helper).
 *
 * Mirrors the gtm.start/dataLayer init that `<GoogleTagManager>` would otherwise
 * emit, so `sendGTMEvent` (which just pushes onto the dataLayer) keeps working.
 */
export function firstPartyGtmLoaderScript(
  containerId: string,
  loaderPath: string,
  dataLayerName = 'dataLayer',
): string {
  return `
(function(w,d,s,l,i){
  w[l]=w[l]||[];
  w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});
  var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),
      dl=l!='dataLayer'?'&l='+l:'';
  j.async=true;
  j.src=${JSON.stringify(loaderPath)}+'?id='+i+dl;
  f.parentNode.insertBefore(j,f);
})(window,document,'script',${JSON.stringify(dataLayerName)},${JSON.stringify(containerId)});
`.trim()
}
